<!-- BgAudio.vue -->
<template>
	<div class="audio-container">
		<p v-if="title" class="audio-title">{{ title }}</p>

		<audio
				ref="audio"
				:controls="controls"
				:loop="loop"
				:preload="preload"
				:muted="mutedOnStart"
				@play="$emit('play')"
				@pause="$emit('pause')"
		>
			<source
					v-for="(s, i) in computedSources"
					:key="i"
					:src="s.src"
					:type="s.type"
			/>
			Ваш браузер не підтримує елемент &lt;audio&gt;.
			<!-- або: Ваш браузер не підтримує елемент <code>audio</code>. -->
		</audio>

			<!-- Кнопка розблокування можна повернути за потреби -->
			<!--
			<button
				v-if="blocked && showUnlockBackdrop"
				class="audioStart"
				:aria-label="unlockAria"
				@click="unlock"
			>
				{{ unlockLabel }}
			</button>
			-->
	</div>
</template>

<script>
export default {
	name: "BgAudio",
	props: {
		sources: {
			type: [ Array, String, Object ],
			default: () => ([
				{ src: "https://adevelopersprofile.com/CodePen/Audio/TheAbandonedManor190673.mp3", type: "audio/mpeg" }
			])
		},
		title: { type: String, default: "Audio Controls:" },
		controls: { type: Boolean, default: true },
		loop: { type: Boolean, default: true },
		preload: { type: String, default: "auto" },
		attemptAutoplay: { type: Boolean, default: true },
		showUnlockBackdrop: { type: Boolean, default: true },
		unlockLabel: { type: String, default: "🔊 Увімкнути" },
		unlockAria: { type: String, default: "Увімкнути звук" },
		mutedOnStart: { type: Boolean, default: false },

		/* ⬇️ НОВЕ: стартова гучність (0..1). 0.2 = 20% */
		startVolume: { type: Number, default: 0.2 }
	},
	data() {
		return {
			blocked: false,
			unlockersBound: false,
			firstUserGestureHandler: null,
			canPlayOnceHandler: null
		};
	},
	computed: {
		computedSources() {
			const toType = (src) => {
				if (src.type) return src.type;
				const u = typeof src === "string" ? src : src.src || "";
				if (/\.mp3(\?|#|$)/i.test(u)) return "audio/mpeg";
				if (/\.ogg(\?|#|$)/i.test(u)) return "audio/ogg";
				if (/\.wav(\?|#|$)/i.test(u)) return "audio/wav";
				return "";
			};
			if (Array.isArray(this.sources)) {
				return this.sources.map(s => (typeof s === "string" ? { src: s, type: toType(s) } : { ...s, type: toType(s) }));
			}
			if (typeof this.sources === "string") {
				return [ { src: this.sources, type: toType(this.sources) } ];
			}
			return [ { src: this.sources.src, type: toType(this.sources) } ];
		}
	},
	mounted() {
		const audio = this.$refs.audio;

		// Виставляємо стартову гучність ще до спроби play
		this.applyStartVolume();

		if (this.attemptAutoplay) {
			if (audio.readyState >= 2) {
				this.tryPlay();
			} else {
				this.canPlayOnceHandler = () => this.tryPlay();
				audio.addEventListener("canplay", this.canPlayOnceHandler, { once: true });
			}
		}
		this.setupUnlockers();
	},
	beforeDestroy() {
		this.cleanupUnlockers();
		const audio = this.$refs.audio;
		if (audio && this.canPlayOnceHandler) {
			audio.removeEventListener("canplay", this.canPlayOnceHandler);
		}
	},
	watch: {
		// Якщо хтось змінить проп ззовні — оновимо гучність
		startVolume() {
			this.applyStartVolume();
		}
	},
	methods: {
		normalizeVolume(v) {
			const n = Number(v);
			if (Number.isNaN(n)) return 0.2;
			return Math.min(1, Math.max(0, n));
		},
		applyStartVolume() {
			const a = this.$refs.audio;
			if (!a) return;
			// На iOS це може ігноруватись — це ок
			a.volume = this.normalizeVolume(this.startVolume);
		},
		async tryPlay() {
			const audio = this.$refs.audio;
			// Перед кожною спробою — гарантуємо стартову гучність
			this.applyStartVolume();
			try {
				await audio.play();
				this.blocked = false;
				this.cleanupUnlockers();
			} catch (e) {
				this.blocked = true;
				this.setupUnlockers();
				this.$emit("autoplay-blocked", e);
			}
		},
		async unlock() {
			const audio = this.$refs.audio;
			// Знімаємо mute і ставимо стартову гучність
			audio.muted = false;
			this.applyStartVolume();
			try {
				await audio.play();
				this.blocked = false;
				this.cleanupUnlockers();
			} catch (_) {
				this.blocked = true;
			}
		},
		setupUnlockers() {
			if (this.unlockersBound) return;
			this.firstUserGestureHandler = () => this.unlock();
			window.addEventListener("click", this.firstUserGestureHandler, { once: true });
			window.addEventListener("keydown", this.firstUserGestureHandler, { once: true });
			window.addEventListener("touchstart", this.firstUserGestureHandler, { once: true, passive: true });
			this.unlockersBound = true;
		},
		cleanupUnlockers() {
			if (!this.unlockersBound) return;
			window.removeEventListener("click", this.firstUserGestureHandler);
			window.removeEventListener("keydown", this.firstUserGestureHandler);
			window.removeEventListener("touchstart", this.firstUserGestureHandler);
			this.unlockersBound = false;
		},
		// публічні API
		play() {
			return this.$refs.audio.play(); 
		},
		pause() {
			return this.$refs.audio.pause(); 
		},
		toggle() {
			const a = this.$refs.audio;
			return a.paused ? a.play() : a.pause();
		}
	}
};
</script>

<style scoped>
.audio-container { position: relative; max-width: 480px; }
.audio-title { margin: 0 0 .5rem; font-weight: 600; }
.audioStart {
	position: absolute; inset: 0;
	display: flex; align-items: center; justify-content: center;
	font: 600 16px/1.2 system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
	background: rgba(0,0,0,.45);
	color: #fff; border: 0; cursor: pointer;
}
.audio-container {
	position: absolute;
	bottom: 20px;
	right: 20px;
	z-index: 100;
}
</style>
